import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, Ban, CheckCircle2, KeyRound, Activity, Copy } from "lucide-react";

const ROLES = ["EDITOR", "ADMIN"];

function InviteDialog({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("EDITOR");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/admin/users/invite", { email, name, role });
      if (data.email_sent) toast.success(`Invite email sent to ${email}`);
      else if (data.invite_link) {
        await navigator.clipboard?.writeText(data.invite_link).catch(() => {});
        toast.warning("Invite created (email skipped). Link copied to clipboard.", {
          description: data.invite_link, duration: 30000,
        });
      }
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to invite");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="invite-dialog">
      <form onSubmit={submit} className="bg-white border max-w-md w-full p-6" style={{ borderColor: "var(--bp-border)" }}>
        <h2 className="font-serif text-2xl mb-4">Invite user</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="invite-email" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full border px-3 py-2 bg-white" style={{ borderColor: "var(--bp-border)" }} data-testid="invite-role">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} data-testid="invite-cancel">Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="invite-submit">{busy ? "Sending…" : "Send invite"}</Button>
        </div>
      </form>
    </div>
  );
}

function ActivityPanel({ userId, userName }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    http.get(`/admin/users/${userId}/activity`)
      .then((r) => setItems(r.data.activity || []))
      .catch(() => setItems([]));
  }, [userId]);
  if (items === null) return <div className="text-sm" style={{ color: "var(--bp-text-muted)" }}>Loading…</div>;
  if (items.length === 0) return <div className="text-sm" style={{ color: "var(--bp-text-muted)" }}>No recorded actions yet for {userName}.</div>;
  return (
    <ul className="space-y-2 text-sm" data-testid="activity-list">
      {items.map((a, i) => (
        <li key={`${a.kind}-${a.at || ""}-${i}`} className="flex items-start gap-3 border-b py-2" style={{ borderColor: "var(--bp-border)" }} data-testid={`activity-${i}`}>
          <span className="uppercase text-[10px] tracking-widest px-1.5 py-0.5 border font-mono mt-0.5" style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}>
            {a.kind.replace(/_/g, " ")}
          </span>
          <div className="flex-1 min-w-0">
            {a.brand && <div className="font-serif">{a.brand.display_name}</div>}
            {a.ref?.url && <div className="text-xs font-mono break-all" style={{ color: "var(--bp-text-muted)" }}>{a.ref.url}</div>}
            {a.ref?.note && <div className="text-xs italic" style={{ color: "var(--bp-text-muted)" }}>{a.ref.note}</div>}
          </div>
          <div className="text-xs font-mono whitespace-nowrap" style={{ color: "var(--bp-text-muted)" }}>{String(a.at).slice(0, 16).replace("T", " ")}</div>
        </li>
      ))}
    </ul>
  );
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [activityFor, setActivityFor] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    http.get("/admin/users")
      .then((r) => { setUsers(r.data.users || []); setForbidden(false); })
      .catch((err) => { if (err.response?.status === 403) setForbidden(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDisabled = async (u) => {
    try {
      if (u.disabled) {
        await http.post(`/admin/users/${u.id}/enable`);
        toast.success(`Re-enabled ${u.name}`);
      } else {
        if (!window.confirm(`Disable ${u.name}? They will not be able to log in. Past actions remain attributed.`)) return;
        await http.post(`/admin/users/${u.id}/disable`);
        toast.success(`Disabled ${u.name}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const triggerReset = async (u) => {
    if (!window.confirm(`Send a password-reset link to ${u.email}?`)) return;
    try {
      const { data } = await http.post(`/admin/users/${u.id}/password-reset`);
      if (data.email_sent) toast.success(`Reset link emailed to ${u.email}`);
      else if (data.reset_link) {
        await navigator.clipboard?.writeText(data.reset_link).catch(() => {});
        toast.warning("Reset link created (email skipped). Link copied to clipboard.", { description: data.reset_link, duration: 30000 });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (forbidden) {
    return (
      <AdminLayout>
        <div className="bp-container py-8" data-testid="users-forbidden">
          <h1 className="font-serif text-4xl mb-4">Users</h1>
          <div className="p-5 border-l-4 bg-red-50" style={{ borderLeftColor: "#B00020" }}>
            <p className="text-sm">This section is restricted to administrators. Signed in as <span className="font-mono">{user?.email}</span> (role: {user?.role}).</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bp-container py-8" data-testid="admin-users-page">
        <div className="flex items-baseline justify-between mb-6">
          <h1 className="font-serif text-4xl">Users</h1>
          <Button onClick={() => setShowInvite(true)} data-testid="btn-invite-user"><UserPlus size={14} className="mr-1" /> Invite user</Button>
        </div>

        <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Name</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Email</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Role</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Status</th>
                <th className="px-4 py-3 uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Last login</th>
                <th className="px-4 py-3 text-right uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <React.Fragment key={u.id}>
                  <tr className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-serif">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.role}</td>
                    <td className="px-4 py-3 text-xs">
                      {u.invite_pending ? (
                        <span className="font-mono text-amber-800">INVITE PENDING</span>
                      ) : u.disabled ? (
                        <span className="font-mono text-red-800">DISABLED</span>
                      ) : (
                        <span className="font-mono text-green-800">ACTIVE</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
                      {u.last_login_at ? String(u.last_login_at).slice(0, 16).replace("T", " ") : "—"}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setActivityFor(activityFor === u.id ? null : u.id)} className="text-xs inline-flex items-center gap-1 px-2 py-1 border hover:bg-neutral-50 mr-1" style={{ borderColor: "var(--bp-border)" }} data-testid={`btn-activity-${u.id}`} title="Activity">
                        <Activity size={12} />
                      </button>
                      <button onClick={() => triggerReset(u)} className="text-xs inline-flex items-center gap-1 px-2 py-1 border hover:bg-neutral-50 mr-1" style={{ borderColor: "var(--bp-border)" }} data-testid={`btn-reset-${u.id}`} title="Password reset">
                        <KeyRound size={12} />
                      </button>
                      <button onClick={() => toggleDisabled(u)} className="text-xs inline-flex items-center gap-1 px-2 py-1 border hover:bg-neutral-50" style={{ borderColor: "var(--bp-border)" }} data-testid={`btn-toggle-${u.id}`} title={u.disabled ? "Enable" : "Disable"}>
                        {u.disabled ? <CheckCircle2 size={12} /> : <Ban size={12} />}
                      </button>
                    </td>
                  </tr>
                  {activityFor === u.id && (
                    <tr className="bg-neutral-50" data-testid={`activity-panel-${u.id}`}>
                      <td colSpan={6} className="px-4 py-4">
                        <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Recent activity — {u.name}</div>
                        <ActivityPanel userId={u.id} userName={u.name} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: "var(--bp-text-muted)" }}>No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showInvite && <InviteDialog onClose={() => setShowInvite(false)} onCreated={load} />}
    </AdminLayout>
  );
}
